import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import authService from './authService';

// Define base API URLs - Hybrid approach like iOS app
const QUESTIONS_API_URL = process.env.REACT_APP_QUESTIONS_API_URL || 'https://know.emraaysolutions.com/api';
const USERS_API_URL = process.env.REACT_APP_USERS_API_URL || 'https://know.emraaysolutions.com/api';

// Create Axios instance for Questions API (Website API)
const questionsApiClient: AxiosInstance = axios.create({
  baseURL: QUESTIONS_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds
});

// Create Axios instance for Users API (EC2 MongoDB API)
const usersApiClient: AxiosInstance = axios.create({
  baseURL: USERS_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds
});

// Request interceptor for Users API - adds Auth0 token only for protected endpoints
usersApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // List of endpoints that don't require authentication
    const publicEndpoints = [
      '/health',
      '/questions',
      '/questions/random',
      '/leaderboard',
      '/timeline/eras',
      '/timeline/events',
      '/conversation/stories',
      '/chat/stories'
    ];
    
    // Check if the request is to a public endpoint
    const isPublicEndpoint = publicEndpoints.some(endpoint => 
      config.url?.includes(endpoint)
    );
    
    // Only add Auth0 token for protected endpoints
    if (!isPublicEndpoint) {
      const accessToken = authService.getAccessToken();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
usersApiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      console.warn('Authentication failed - token may be expired');
      // Clear auth data and redirect to login
      authService.logout();
      // The Auth0Provider will handle the redirect
    }
    
    return Promise.reject(error);
  }
);

// Also add interceptor to Questions API for protected endpoints
questionsApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // List of endpoints that don't require authentication
    const publicEndpoints = [
      '/health',
      '/questions',
      '/questions/random',
      '/leaderboard',
      '/timeline/eras',
      '/timeline/events',
      '/conversation/stories',
      '/chat/stories'
    ];
    
    // Check if the request is to a public endpoint
    const isPublicEndpoint = publicEndpoints.some(endpoint => 
      config.url?.includes(endpoint)
    );
    
    // Only add Auth0 token for protected endpoints
    if (!isPublicEndpoint) {
      const accessToken = authService.getAccessToken();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Legacy API client for backward compatibility
const apiClient: AxiosInstance = usersApiClient;

// Export both API clients
export { questionsApiClient, usersApiClient };
export default apiClient; 